import classNames from 'classnames';
import {observer} from 'mobx-react';
import React from 'react';

import {TrackerInitializer} from '@udemy/event-tracking';
import {Footer} from '@udemy/footer';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {Header, SearchBarMXEnum} from '@udemy/react-header';
import {LinkBar} from '@udemy/react-link-bar';
import {Toaster} from '@udemy/react-messaging-components';
import {useStores} from '@udemy/store-provider';
import {useUDData} from '@udemy/ud-data';

import {NEW_SEARCH_BAR_MX} from 'src/lib/experimentation-constants';
import {RootStore} from 'src/stores/root-store';
import {HomeContextData} from 'src/types/home-context';

import styles from './layout.module.less';

export const getLayout = (page: React.ReactElement) => {
    return <Layout>{page}</Layout>;
};

/** Exported for unit testing */
export const Layout = observer(({children}: {children: React.ReactElement}) => {
    const [rootStore] = useStores([RootStore]);
    const udData = useUDData();
    const {isLoading, featureVariantAssignments: features} = useFeatures([NEW_SEARCH_BAR_MX]);
    const showNewSearchBar = features.get('new_search_bar_mx')?.configuration.showNewSearchBarMX;
    // We do not show Link bar to users with an active consumer subscription (e.g. Personal Plan) or unauthenticated users.
    // We also need to apply header style override if link bar is shown.
    const hasConsumerSubscription =
        rootStore.headerData?.user?.consumer_subscription_active ?? false;
    const showLinkBar = !hasConsumerSubscription && udData.me.is_authenticated;
    const categories = (rootStore.contextData as HomeContextData)?.categories;
    let showTwoRowSearchBarMX = SearchBarMXEnum.NotLoaded;
    if (!isLoading) {
        showTwoRowSearchBarMX = showNewSearchBar ? SearchBarMXEnum.NewBar : SearchBarMXEnum.OldBar;
    }

    return (
        <>
            {/* TODO: remove SmartBar rendering from Header component, re-add it here */}
            {/* <SmartBar /> */}
            <div
                className={classNames(styles['container'], {
                    [styles['container-mx']]: showLinkBar,
                })}
            >
                <Header {...rootStore.headerData} showTwoRowSearchBarMX={showTwoRowSearchBarMX} />
                {showLinkBar && categories && (
                    <LinkBar
                        isCategoryExpanded={true}
                        categories={categories}
                        goToContentAnchorId="main-content-anchor"
                    />
                )}
                <main className={styles['content']}>{children}</main>
                <Footer
                    clientProps={rootStore.footerData}
                    serverProps={rootStore.footerData}
                    limitedConsumptionTrial={true}
                />
                <Toaster />
            </div>
            <TrackerInitializer />
        </>
    );
});
